import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routesModel";
import { useUser } from "../../users/providers/UserProvider";
import { GetUser } from "../../users/service/userApi";
import useHandleUsers from "../../users/hooks/useHandleUsers";
import { useFeedback } from "../../feedback/FeedbackProvider";
import { Z } from "../../styles/zLayers";
import { dvh } from "../../styles/viewportHeight";
import { useDismissible } from "../../native/dismissibleStack";
import { isNativePlatform } from "../../lib/nativeAuth";
import {
  canStartDrag,
  dragOffset,
  dragVelocity,
  shouldDismiss,
  DRAG_START_SLOP,
} from "../sheetDrag";

/** Shared by the in-app policy links and the external Support mailto. */
const legalLinkStyle = { fontSize: 13, color: "#64748b", textDecoration: "none" };

/**
 * Mobile account menu: profile, edit, admin (if applicable), logout.
 * Renders fixed backdrop + bottom sheet when open is true.
 */
const ProfileAccountSheet = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  // The sign-in token carries no email, so `user.email` was always blank here
  // and on a shared phone nobody could see which account was signed in. Read
  // it from the owner's own profile when the sheet opens. (Code review
  // IDENTITY-9 / SEPARATION-10)
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (!open || !user?._id) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => GetUser(user._id))
      .then((u) => {
        if (!cancelled && String(u?._id) === String(user._id)) setEmail(u?.email || "");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, user?._id]);
  const { handleLogout } = useHandleUsers();
  const { openFeedback } = useFeedback();

  // Drag-to-dismiss. The handle has always LOOKED draggable; now it is.
  const sheetRef = useRef(null);
  const openerRef = useRef(null);
  const scrollRef = useRef(null);
  const gesture = useRef(null);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);

  const endGesture = useCallback(() => {
    gesture.current = null;
    setDragging(false);
    setDragY(0);
  }, []);

  const onPointerDown = useCallback(
    (e, fromHandle) => {
      // Only a primary pointer starts a drag; a second finger (or a mouse
      // right-button) must never hijack the sheet mid-gesture.
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (!canStartDrag({ fromHandle, scrollTop: scrollRef.current?.scrollTop ?? 0 })) return;
      gesture.current = { startY: e.clientY, startedAt: Date.now(), active: false };
    },
    []
  );

  const onPointerMove = useCallback((e) => {
    const g = gesture.current;
    if (!g) return;
    const dy = e.clientY - g.startY;
    // Below the slop it is still a tap, not a drag — so tapping a menu row
    // never nudges the sheet.
    if (!g.active && dy < DRAG_START_SLOP) return;
    g.active = true;
    setDragging(true);
    setDragY(dragOffset(dy));
  }, []);

  const onPointerUp = useCallback(
    (e) => {
      const g = gesture.current;
      if (!g) return;
      const dy = e.clientY - g.startY;
      const elapsedMs = Date.now() - g.startedAt;
      const sheetHeight = sheetRef.current?.offsetHeight || 0;
      const velocity = dragVelocity({ dy, elapsedMs });
      const dismiss = g.active && shouldDismiss({ dy, sheetHeight, velocity });
      endGesture();
      if (dismiss) onClose();
      // Otherwise dragY resets to 0 and the sheet springs back into place.
    },
    [endGesture, onClose]
  );

  // An interrupted gesture (call, notification shade, another finger) must not
  // leave the sheet stranded half-dragged.
  const onPointerCancel = useCallback(() => endGesture(), [endGesture]);

  // Android hardware Back closes the sheet instead of leaving the screen — the
  // sheet is the topmost thing, so it is what Back should dismiss.
  useDismissible(open, onClose, "profile-account-sheet");

  // The web half of the same idea, and web-ONLY.
  //
  // It listens for a browser Back and closes the sheet. What it no longer does
  // is SYNTHESISE the entry that Back pops (P2-22). It used to push
  // `{ accountSheet: true }` on open, and that entry was only ever spent when
  // the close came from Back — the backdrop, the drag-to-dismiss, the menu
  // rows and Log Out all ran the cleanup and left it sitting on the stack. One
  // dead entry per open, so the user's next Back press popped a corpse and
  // nothing moved; the press after that finally navigated. Open the sheet three
  // times and Back is deaf three times.
  //
  // Pushing nothing is what makes that unfixable-by-bookkeeping problem go
  // away: there is no entry to pop, on any close path, ever. The cost is that a
  // browser Back now goes back AND closes the sheet rather than only closing
  // it — which is exactly what the other twenty-odd overlays in this app
  // already do (none of them touch history either, see dismissibleStack), and
  // it is the honest reading: the sheet is chrome, not a route.
  //
  // Still off inside the Capacitor wrap. A pushed entry carries no React Router
  // `idx`, and `idx` is exactly what the native handler reads to decide whether
  // there is anywhere to go back to; on native the stack registration above is
  // the whole mechanism, and a second listener here would double-close.
  useEffect(() => {
    if (!open || isNativePlatform()) return undefined;
    const onPopState = () => onClose();
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [open, onClose]);

  // This sheet tells assistive tech `aria-modal="true"`, which asks it to treat
  // everything outside as hidden — and then moved focus nowhere, so a keyboard
  // or screen-reader user was left standing on whatever opened it (the avatar
  // button in the header), a node the sheet had just declared invisible. A
  // screen reader announces nothing on open, and the user's position in the
  // document is a node that is no longer supposed to exist.
  //
  // NOT claimed, because it was measured FALSE for this sheet: that the next
  // Tab walks the page BEHIND it. `TopAccountBar` renders the sheet as the
  // avatar's immediate next sibling, so Tab from the avatar has always landed
  // on the first row inside the sheet.
  //
  // THIS IS NOT A FOCUS TRAP AND MUST NOT BE READ AS ONE. Measured from the
  // initial focus position: Shift+Tab reaches the opener in ONE press — the
  // very node the paragraph above calls hidden — and nine forward Tabs reach
  // the page behind. Moving focus IN is the announce fix; CONTAINING it is
  // separate work, and is filed rather than claimed here.
  //
  // Same remedy as `MatchRevealMoment` (its effect at :260-276, plus the
  // `tabIndex={-1}` at :337 that makes it work). The sheet ROOT takes focus
  // rather than the first control so nothing arrives pre-armed under Enter:
  // six of the eight rows navigate away on a single press, and the two that do
  // not ("Send feedback", "Support") still act immediately.
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    sheetRef.current?.focus();
    return () => {
      // Hand focus back where it came from. `go()` and `handleLogoutClick()`
      // navigate as they close, so the opener really can be gone by then.
      //
      // The `document.contains` arm is DEFENSIVE, NOT LOAD-BEARING, and is
      // labelled that way deliberately: `focus()` on a detached node does not
      // throw, it is simply a no-op that leaves `activeElement` on `<body>` —
      // the same place omitting the call leaves it. Deleting this arm changes
      // no observable behaviour and kills none of the tests below (measured).
      // `check-backlog.md`'s [#103] sprint-55 block says the same thing: only a
      // spy can see the difference, so do not claim it is load-bearing. It is
      // kept because it states the intent, not because it is doing work.
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && opener !== document.body && document.contains(opener)) {
        opener.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  const handleFeedbackClick = () => {
    onClose();
    openFeedback();
  };

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const handleLogoutClick = () => {
    onClose();
    handleLogout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: Z.modal,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
        }}
      />
      <div
        ref={sheetRef}
        data-testid="account-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Account menu"
        // A <div> is not focusable without this, so the effect above would call
        // focus() to no effect and leave the user on `<body>` exactly as before.
        tabIndex={-1}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          // No transition WHILE dragging (the sheet must track the finger with
          // no lag); a short one on release so it springs back.
          transition: dragging ? "none" : "transform 220ms cubic-bezier(0.2,0.8,0.2,1)",
          // touch-action is resolved by INTERSECTING this element's value with
          // every one of its ancestors', so whatever stands here is a ceiling on
          // what anything inside the sheet is allowed to do. This used to be
          // `none`, which read as "the drag handlers own every gesture in this
          // sheet" and actually meant the scroll container below could not be
          // panned by a finger AT ALL (P2-31): with a long admin menu the rows
          // past the fold — Log Out last of them — were unreachable on a phone.
          // A wheel is not a touch, so desktop kept scrolling and hid it.
          //
          // A gesture's touch-action is computed ONCE, at touch-start, from the
          // element the finger lands on upward — not from wherever the events
          // are later handled. So the handle's own `none` (below) is the whole
          // of what a handle drag needs, even though the pointermove/pointerup
          // handlers live up here on the root: `none` ∩ anything is `none`, and
          // the resolved value holds for the rest of that gesture.
          //
          // `pan-y` rather than `auto` because this sheet has never answered a
          // horizontal swipe or a pinch; it keeps the intersection open for the
          // scroll container's own `pan-y` while no longer being a ceiling of
          // zero.
          touchAction: "pan-y",
          // Bounded so a long admin menu scrolls INSIDE the sheet instead of
          // pushing Log Out past the bottom of the screen, and so the Android
          // navigation bar never covers the last row. dvh(85), not "85dvh",
          // or a Chrome 90 WebView drops the bound altogether.
          maxHeight: dvh(85),
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          // modalContent, not snackbar level — a snack must be able to surface
          // above this sheet, not tie with it.
          zIndex: Z.modalContent,
          background: "rgba(18,16,22,0.98)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.5)",
          border: "1px solid rgba(255,255,255,0.08)",
          padding: "12px 0 max(40px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
          fontFamily: "Inter, sans-serif",
          animation: "profileSheetSlideUp 0.25s ease",
        }}
      >
        {/* The handle is the dedicated drag region: it works even when the menu
            below is scrolled, which is what makes a long admin menu both
            scrollable AND dismissable. */}
        <div
          data-testid="account-sheet-handle"
          aria-label="Drag to close"
          onPointerDown={(e) => onPointerDown(e, true)}
          style={{
            width: 36,
            height: 4,
            borderRadius: 99,
            background: "rgba(255,255,255,0.2)",
            margin: "0 auto 20px",
            flexShrink: 0,
            cursor: "grab",
            // The one spot where the gesture genuinely has to be taken away
            // from the browser: a drag starting here must arrive as
            // pointermove rather than being spent scrolling something. `none`
            // still wins the ancestor intersection from here, but it is now
            // scoped to this bar — a ~24px-tall strip — instead of the whole
            // sheet, so it can no longer freeze the menu underneath (P2-31).
            touchAction: "none",
            // A 4px bar is far too small a target; pad the hit area without
            // changing how it looks.
            padding: "10px 24px",
            backgroundClip: "content-box",
            boxSizing: "content-box",
          }}
        />

        {/* Everything below the handle scrolls. A drag started in here only
            dismisses when the content is already at the top (see canStartDrag),
            so the admin menu stays scrollable.

            On a touchscreen the browser now usually claims a vertical drag in
            here as a scroll and answers with pointercancel, which endGesture
            already handles — so the handle, the backdrop and Back are the
            dependable ways to close. canStartDrag's at-the-top rule stays
            exactly as it was: it is what still gates the cases the browser does
            NOT claim (a mouse drag, a pen, a pan the scroller declines). */}
        <div
          ref={scrollRef}
          data-testid="account-sheet-scroll"
          onPointerDown={(e) => onPointerDown(e, false)}
          style={{
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            WebkitOverflowScrolling: "touch",
            // Vertical panning is the entire job of this container. Worth
            // knowing that this line is powerless on its own: a descendant
            // cannot re-enable a gesture an ancestor removed, so dropping
            // `none` from the root above is not optional decoration, it is
            // half of the fix (P2-31).
            touchAction: "pan-y",
            // And now that a finger can pan in here, stop that pan from
            // chaining out to the page behind the modal — or arming
            // pull-to-refresh — once the menu hits either end.
            overscrollBehavior: "contain",
          }}
        >

        {user && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "0 24px 20px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              marginBottom: 8,
            }}
          >
            {user.image?.url ? (
              <img
                src={user.image.url}
                alt=""
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid #a855f7",
                }}
              />
            ) : (
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #a855f7, #7c3aed)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  color: "#fff",
                  fontWeight: 700,
                }}
              >
                {Array.from(user.name?.first || "")[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div>
              <div style={{ color: "#f1f5f9", fontWeight: 700, fontSize: 16 }}>
                <bdi>{`${user.name?.first || ""} ${user.name?.last || ""}`.trim()}</bdi>
              </div>
              {email && <div data-testid="account-sheet-email" style={{ color: "#64748b", fontSize: 13 }}>{email}</div>}
            </div>
          </div>
        )}

        <button
          type="button"
          data-testid="sheet-my-profile"
          onClick={() => go(`${ROUTES.PROFILE}/${user?._id}`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            width: "100%",
            padding: "16px 24px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#e2e8f0",
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 500,
          }}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          My Profile
        </button>

        <button
          type="button"
          data-testid="sheet-market"
          onClick={() => go(ROUTES.POKEMON_CARDS)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            width: "100%",
            padding: "16px 24px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#e2e8f0",
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 500,
          }}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
            />
          </svg>
          Browse Market
        </button>

        <button
          type="button"
          onClick={() => go(ROUTES.EDIT_USER)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            width: "100%",
            padding: "16px 24px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#e2e8f0",
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 500,
          }}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Settings / Edit Profile
        </button>

        {user?.isAdmin && (
          <>
            <div style={{ margin: "8px 24px", borderTop: "1px solid rgba(255,255,255,0.06)" }} />
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              Admin — Users
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_SAFE_SPOTS)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Admin — Safe Spots
            </button>
            <button
              type="button"
              data-testid="admin-nav-settings"
              onClick={() => go(ROUTES.ADMIN_SETTINGS)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Admin — Beta Settings
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_TRADES)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4"
                />
              </svg>
              Admin — Trades
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_EMAILS)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Admin — Emails
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_MODERATION)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Admin — Image moderation
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_REPORTS)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
              </svg>
              Admin — Reports
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_ERRORS)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
              </svg>
              Admin — Client errors
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_FEEDBACK)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l1.3-3.9A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Admin — Feedback
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_DELETION_REQUESTS)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Admin — Deletion requests
            </button>
            <button
              type="button"
              onClick={() => go(ROUTES.ADMIN_CHALLENGE_REVIEW)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                width: "100%",
                padding: "16px 24px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#fca5a5",
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Challenge evidence review
            </button>
          </>
        )}

        <div style={{ margin: "8px 24px", borderTop: "1px solid rgba(255,255,255,0.06)" }} />

        <button
          type="button"
          onClick={handleFeedbackClick}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            width: "100%",
            padding: "16px 24px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#e2e8f0",
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 500,
          }}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l1.3-3.9A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Send feedback
        </button>

        <div style={{ display: "flex", gap: 16, padding: "12px 24px" }}>
          {[
            // P2-60. These two used to be absolute https://tradeloft.app/...
            // links opened with target="_blank". This sheet is the ONLY policy
            // entry point a signed-in user has — the bottom nav and the top bar
            // expose none — so that combination threw them out of the app, into
            // the system browser, at a host the codebase itself records as not
            // resolving (#26). Google Play requires the privacy policy to be
            // reachable. The documents already render in-app at /privacy and
            // /terms; this is the same shape LoginPage's signed-out footer
            // uses. Support stays external because a mailto genuinely is.
            { label: "Privacy", to: ROUTES.PRIVACY },
            { label: "Terms", to: ROUTES.TERMS },
            { label: "Support", href: "mailto:support@tradeloft.app" },
          ].map(({ label, href, to }) =>
            to ? (
              // A real href, so the row stays keyboard- and right-click-
              // friendly; the click itself is handed to the router (via `go`,
              // which closes the sheet first) rather than to the browser.
              <a
                key={label}
                href={to}
                onClick={(e) => {
                  e.preventDefault();
                  go(to);
                }}
                style={legalLinkStyle}
              >
                {label}
              </a>
            ) : (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={legalLinkStyle}
              >
                {label}
              </a>
            )
          )}
        </div>

        <button
          type="button"
          onClick={handleLogoutClick}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            width: "100%",
            padding: "16px 24px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#f87171",
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          Log Out
        </button>
        </div>
      </div>

      <style>{`
        @keyframes profileSheetSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ProfileAccountSheet;
